import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { addWatchlistSymbol } from '../services/watchlistApi';
import {
  antiChaseFromFactors,
  createRankStrategy,
  deleteRankStrategy,
  fetchMarketUniverseRank,
  fetchRankBacktest,
  fetchRankStrategies,
  fetchSuggestionOutcomes,
  logQuantSuggestionAdd,
  proposeRankStrategy,
  QUANT_RANK_POLL_MS,
  ruleBreakerBreakdown,
  updateRankStrategy,
  type BuiltinRankStrategyId,
  type CustomRankStrategy,
  type RankBacktestResult,
  type RankStrategyId,
  type SuggestionOutcomesSummary
} from '../services/quantAgiRankService';

const STRATEGY_STORAGE_KEY = 'kib-dashboard-quant-rank-strategy';

const BUILTIN_TABS: { id: BuiltinRankStrategyId; label: string }[] = [
  { id: 'momentum_liquidity', label: 'Momentum / liquidity' },
  { id: 'photonics_chokepoint', label: 'AI photonics chokepoint' },
  { id: 'rule_breaker_gardner_early', label: 'Gardner Early (lower cap)' },
  { id: 'rule_breaker_gardner', label: 'Rule Breaker (Gardner)' }
];

function isBuiltinId(id: string): id is BuiltinRankStrategyId {
  return BUILTIN_TABS.some((t) => t.id === id);
}

function strategyApiError(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { message?: string; errors?: { msg?: string }[] } | undefined;
    if (data?.message) return String(data.message);
    const firstErr = data?.errors?.[0]?.msg;
    if (firstErr) return String(firstErr);
    return String(e.message || fallback);
  }
  if (e instanceof Error) return e.message;
  return fallback;
}

const HORIZON_LABELS: { key: string; label: string }[] = [
  { key: '3m', label: '3 mo' },
  { key: '6m', label: '6 mo' },
  { key: '12m', label: '12 mo' }
];

function pctClass(v: number | null | undefined): string {
  if (v == null) return 'text-kib-muted';
  if (v > 0) return 'text-emerald-400';
  if (v < 0) return 'text-red-400';
  return 'text-kib-muted';
}

function fmtPct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}

const tabBtn = (active: boolean) =>
  `rounded-md px-2.5 py-1.5 text-[11px] font-semibold transition-colors sm:text-xs ${
    active
      ? 'bg-white/[0.14] text-kib-fg ring-1 ring-white/[0.12]'
      : 'text-kib-muted hover:bg-white/[0.06] hover:text-kib-fg'
  }`;

function BacktestHorizonTable({
  title,
  horizons,
  note
}: {
  title: string;
  horizons: RankBacktestResult['holdout'] | RankBacktestResult['trailing'];
  note?: string;
}) {
  if (!horizons?.horizons) return null;
  return (
    <div className="rounded-md border border-white/[0.06] bg-black/20 px-3 py-2">
      <p className="text-[11px] font-semibold text-kib-fg">{title}</p>
      {note ? <p className="mt-1 text-[10px] leading-snug text-kib-muted">{note}</p> : null}
      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[280px] text-[10px]">
          <thead>
            <tr className="text-left text-kib-muted">
              <th className="pb-1 pr-2 font-medium">Window</th>
              <th className="pb-1 pr-2 font-medium">Top 5 basket</th>
              <th className="pb-1 pr-2 font-medium">SPY</th>
              <th className="pb-1 font-medium">Excess</th>
            </tr>
          </thead>
          <tbody>
            {HORIZON_LABELS.map(({ key, label }) => {
              const row = horizons.horizons?.[key];
              if (!row?.ok) {
                return (
                  <tr key={key} className="border-t border-white/[0.04] text-kib-muted">
                    <td className="py-1 pr-2">{label}</td>
                    <td colSpan={3} className="py-1">
                      —
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={key} className="border-t border-white/[0.04]">
                  <td className="py-1 pr-2 text-kib-muted">{label}</td>
                  <td className={`py-1 pr-2 tabular-nums ${pctClass(row.basket_return_pct)}`}>
                    {fmtPct(row.basket_return_pct)}
                  </td>
                  <td className={`py-1 pr-2 tabular-nums ${pctClass(row.benchmark_return_pct)}`}>
                    {fmtPct(row.benchmark_return_pct)}
                  </td>
                  <td className={`py-1 tabular-nums ${pctClass(row.excess_return_pct)}`}>
                    {fmtPct(row.excess_return_pct)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const QuantAgiSuggestionsPanel: React.FC<{
  onSymbolAdded?: () => void;
}> = ({ onSymbolAdded }) => {
  const [strategyId, setStrategyId] = useState<RankStrategyId>(() => {
    if (typeof window === 'undefined') return 'momentum_liquidity';
    const saved = window.localStorage.getItem(STRATEGY_STORAGE_KEY) || '';
    if (isBuiltinId(saved) || /^custom:\d+$/.test(saved)) return saved;
    return 'momentum_liquidity';
  });
  const [customStrategies, setCustomStrategies] = useState<CustomRankStrategy[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formName, setFormName] = useState('');
  const [formBusy, setFormBusy] = useState(false);
  const [grokPrompt, setGrokPrompt] = useState('');
  const [grokBusy, setGrokBusy] = useState(false);
  const [grokRationale, setGrokRationale] = useState<string | null>(null);
  /** Hidden engine mapping from Grok — not shown in the form. */
  const [draftBase, setDraftBase] = useState<BuiltinRankStrategyId>('rule_breaker_gardner_early');
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [strategyLabel, setStrategyLabel] = useState('');
  const [disclaimer, setDisclaimer] = useState('');
  const [universeMeta, setUniverseMeta] = useState<{
    mode?: string;
    dynamic_added?: number;
    universe_size?: number;
  } | null>(null);
  const [meta, setMeta] = useState<{
    accepted_count: number;
    excluded_count: number;
    min_price: number;
    min_avg_dollar_vol_20d: number;
  } | null>(null);
  const [positions, setPositions] = useState<
    Awaited<ReturnType<typeof fetchMarketUniverseRank>>['positions']
  >([]);
  const [adding, setAdding] = useState<Record<string, boolean>>({});
  const [added, setAdded] = useState<Record<string, boolean>>({});
  const [backtest, setBacktest] = useState<RankBacktestResult | null>(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestError, setBacktestError] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<SuggestionOutcomesSummary | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const backtestAbortRef = useRef<AbortController | null>(null);

  const loadRank = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const topN =
        customStrategies.find((c) => c.key === strategyId)?.params.topN || 25;
      const result = await fetchMarketUniverseRank(strategyId, topN, controller.signal);
      if (controller.signal.aborted) return;
      setPositions(result.positions);
      setMeta(result.meta);
      setUniverseMeta(result.universeMeta ?? null);
      setStrategyLabel(result.strategyMeta.label);
      setDisclaimer(result.strategyMeta.disclaimer);
      setConnected(true);
      setLastFetchedAt(new Date().toISOString());
    } catch (e) {
      if (controller.signal.aborted || axios.isCancel(e)) return;
      setConnected(false);
      const msg = axios.isAxiosError(e)
        ? String(e.response?.data?.message || e.message)
        : e instanceof Error
          ? e.message
          : 'Could not load Quant AGI ranks';
      setError(msg);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [strategyId, customStrategies]);

  const loadBacktest = useCallback(async () => {
    backtestAbortRef.current?.abort();
    const controller = new AbortController();
    backtestAbortRef.current = controller;
    setBacktestLoading(true);
    setBacktestError(null);
    try {
      const data = await fetchRankBacktest(strategyId, 5, controller.signal);
      if (controller.signal.aborted) return;
      setBacktest(data);
    } catch (e) {
      if (controller.signal.aborted || axios.isCancel(e)) return;
      setBacktest(null);
      setBacktestError(e instanceof Error ? e.message : 'Backtest unavailable');
    } finally {
      if (!controller.signal.aborted) setBacktestLoading(false);
    }
  }, [strategyId]);

  const loadOutcomes = useCallback(async () => {
    try {
      const data = await fetchSuggestionOutcomes(12);
      setOutcomes(data);
    } catch {
      setOutcomes(null);
    }
  }, []);

  const loadCustomStrategies = useCallback(async () => {
    try {
      const pack = await fetchRankStrategies();
      setCustomStrategies(pack.custom);
      return pack.custom;
    } catch {
      setCustomStrategies([]);
      return [] as CustomRankStrategy[];
    }
  }, []);

  const openCreateEditor = () => {
    setEditingId(null);
    setFormName('');
    setDraftBase(
      isBuiltinId(strategyId) ? strategyId : 'rule_breaker_gardner_early'
    );
    setGrokPrompt('');
    setGrokRationale(null);
    setEditorOpen(true);
  };

  const openEditEditor = (row: CustomRankStrategy) => {
    setEditingId(row.id);
    setFormName(row.name);
    setDraftBase(row.basePreset);
    setGrokPrompt('');
    setGrokRationale(null);
    setEditorOpen(true);
  };

  /** One-shot: Grok maps the idea → create with open gates + topN 25. */
  const onBuildStrategy = async () => {
    const idea = grokPrompt.trim();
    if (idea.length < 3) {
      toast.error('Describe the strategy you want to build');
      return;
    }
    setGrokBusy(true);
    setFormBusy(true);
    try {
      const { proposal, runMetadata } = await proposeRankStrategy(idea);
      const base = proposal.basePreset;
      const rawName = (proposal.name || '').trim();
      const looksUrl = /^https?:\/\//i.test(rawName) || /^https?:\/\//i.test(idea.trim());
      const name = (
        looksUrl && (!rawName || /^https?:\/\//i.test(rawName))
          ? ''
          : rawName || idea
      )
        .trim()
        .slice(0, 48) || 'Custom research';
      setFormName(name);
      setDraftBase(base);
      setGrokRationale(proposal.rationale || null);

      if (editingId != null) {
        const updated = await updateRankStrategy(editingId, {
          name,
          basePreset: base,
          minPrice: 0,
          minAvgDollarVol20d: 0,
          topN: 25
        });
        toast.success('Strategy updated');
        await loadCustomStrategies();
        setStrategyId(updated.key);
      } else {
        const created = await createRankStrategy({
          name,
          basePreset: base,
          minPrice: 0,
          minAvgDollarVol20d: 0,
          topN: 25
        });
        const via = runMetadata?.fallbackUsed
          ? 'template'
          : runMetadata?.providerUsed || 'Grok';
        toast.success(`Strategy built (${via})`);
        await loadCustomStrategies();
        setStrategyId(created.key);
      }
      setEditorOpen(false);
    } catch (e) {
      toast.error(strategyApiError(e, 'Could not build strategy'));
    } finally {
      setGrokBusy(false);
      setFormBusy(false);
    }
  };

  const onSaveStrategy = async () => {
    const name = formName.trim();
    if (!name) {
      toast.error('Name is required');
      return;
    }
    if (editingId == null) {
      toast.error('Describe your idea and tap Build strategy');
      return;
    }
    setFormBusy(true);
    try {
      const updated = await updateRankStrategy(editingId, {
        name,
        basePreset: draftBase,
        minPrice: 0,
        minAvgDollarVol20d: 0,
        topN: 25
      });
      toast.success('Strategy updated');
      await loadCustomStrategies();
      setStrategyId(updated.key);
      setEditorOpen(false);
    } catch (e) {
      toast.error(strategyApiError(e, 'Could not save strategy'));
    } finally {
      setFormBusy(false);
    }
  };

  const onDeleteStrategy = async (row: CustomRankStrategy) => {
    if (!window.confirm(`Delete research strategy “${row.name}”? Built-ins are never deleted.`)) {
      return;
    }
    setFormBusy(true);
    try {
      await deleteRankStrategy(row.id);
      toast.success('Strategy deleted');
      const remaining = await loadCustomStrategies();
      if (strategyId === row.key) {
        setStrategyId(remaining[0]?.key || 'momentum_liquidity');
      }
      setEditorOpen(false);
    } catch (e) {
      const msg = axios.isAxiosError(e)
        ? String(e.response?.data?.message || e.message)
        : e instanceof Error
          ? e.message
          : 'Could not delete strategy';
      toast.error(msg);
    } finally {
      setFormBusy(false);
    }
  };

  useEffect(() => {
    void loadCustomStrategies();
  }, [loadCustomStrategies]);

  useEffect(() => {
    void loadRank();
    const id = setInterval(() => void loadRank(), QUANT_RANK_POLL_MS);
    return () => {
      clearInterval(id);
      abortRef.current?.abort();
    };
  }, [loadRank]);

  useEffect(() => {
    void loadBacktest();
    return () => backtestAbortRef.current?.abort();
  }, [loadBacktest]);

  useEffect(() => {
    void loadOutcomes();
  }, [loadOutcomes]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STRATEGY_STORAGE_KEY, strategyId);
    } catch {
      /* ignore */
    }
  }, [strategyId]);

  const selectedCustom = customStrategies.find((c) => c.key === strategyId) || null;

  const onAdd = async (symbol: string, rankScore: number, rankPosition: number, entryPrice: number | null) => {
    setAdding((s) => ({ ...s, [symbol]: true }));
    try {
      await addWatchlistSymbol(symbol, 'stock');
      try {
        await logQuantSuggestionAdd({
          symbol,
          strategy: strategyId,
          rankScore,
          rankPosition,
          entryPrice
        });
        void loadOutcomes();
      } catch {
        /* non-blocking */
      }
      setAdded((s) => ({ ...s, [symbol]: true }));
      toast.success(`${symbol} added to watchlist`);
      onSymbolAdded?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : `Failed to add ${symbol}`;
      toast.error(msg);
    } finally {
      setAdding((s) => ({ ...s, [symbol]: false }));
    }
  };

  const showEmptyLoading = loading && positions.length === 0;

  return (
    <section
      id="quant-agi-suggestions"
      className="scroll-mt-20 rounded-lg border border-white/[0.08] bg-kib-card p-4 sm:p-5"
    >
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold tracking-tight text-kib-fg sm:text-xl">
              Quant AGI stock suggestions
            </h2>
            <span
              className={`rounded-md px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                connected
                  ? 'bg-emerald-500/15 text-emerald-300'
                  : error
                    ? 'bg-amber-500/15 text-amber-200'
                    : 'bg-white/[0.06] text-kib-muted'
              }`}
            >
              {loading && !connected ? 'Loading' : connected ? 'Live' : 'Offline'}
            </span>
          </div>
          <p className="mt-1 text-sm text-kib-muted">
            {strategyLabel || 'Preset strategy rank — educational tooling only'}
          </p>
          <p className="mt-1 text-[11px] text-kib-muted">
            {lastFetchedAt ? (
              <>
                Updated {new Date(lastFetchedAt).toLocaleTimeString()} · refreshes every{' '}
                {QUANT_RANK_POLL_MS / 1000}s
              </>
            ) : (
              'Fetching ranks from Quant AGI…'
            )}
          </p>
          {universeMeta?.mode === 'static_plus_dynamic' ? (
            <p className="mt-1 text-[11px] text-kib-muted">
              Universe: {universeMeta.universe_size ?? '—'} symbols ({universeMeta.dynamic_added ?? 0} from Massive
              screener + static list). Anti-chase guard penalizes extended names.
            </p>
          ) : null}
          <p className="mt-1 text-[11px] text-kib-muted">
            <Link to="/quant-agi" className="text-kib-cyber underline-offset-2 hover:underline">
              Open Quant terminal
            </Link>{' '}
            for autoresearch, coding advisor, and ops cockpit.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadRank()}
          disabled={loading}
          className="btn-secondary shrink-0 px-3 py-1.5 text-xs disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="mb-3 space-y-2">
        <div className="w-full max-w-4xl rounded-lg border border-white/[0.08] bg-black/25 p-2">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-kib-muted">
              Strategies
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openCreateEditor}
                className="btn-secondary min-h-[40px] px-3 text-xs sm:min-h-[36px]"
              >
                + New strategy
              </button>
              {selectedCustom ? (
                <>
                  <button
                    type="button"
                    onClick={() => openEditEditor(selectedCustom)}
                    className="btn-secondary min-h-[40px] px-3 text-xs sm:min-h-[36px]"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    disabled={formBusy}
                    onClick={() => void onDeleteStrategy(selectedCustom)}
                    className="min-h-[40px] px-2 text-xs text-rose-300 hover:underline disabled:opacity-50 sm:min-h-[36px]"
                  >
                    Delete
                  </button>
                </>
              ) : null}
            </div>
          </div>
          <div
            className="flex flex-wrap gap-1"
            role="tablist"
            aria-label="Quant rank strategies"
          >
            {customStrategies.map((c) => (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={strategyId === c.key}
                onClick={() => setStrategyId(c.key)}
                className={tabBtn(strategyId === c.key)}
                title="Your research strategy — Edit to tweak"
              >
                {c.name}
              </button>
            ))}
            {customStrategies.length > 0 ? (
              <span
                className="mx-0.5 hidden h-6 w-px self-center bg-white/10 sm:inline-block"
                aria-hidden
              />
            ) : null}
            {BUILTIN_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={strategyId === tab.id}
                onClick={() => setStrategyId(tab.id)}
                className={tabBtn(strategyId === tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        {selectedCustom ? (
          <p className="text-[10px] text-kib-muted">
            Your research · top 25 · tap Edit to rename or rebuild with Grok
          </p>
        ) : (
          <p className="text-[10px] text-kib-muted">
            Built-ins are read-only. + New strategy builds one with Grok — it appears in this box.
          </p>
        )}
      </div>

      {editorOpen ? (
        <div className="mb-3 rounded-lg border border-kib-cyber/30 bg-kib-surface px-3 py-3">
          <p className="text-xs font-semibold text-kib-fg">
            {editingId != null ? 'Edit research strategy' : 'Build research strategy'}
          </p>
          <p className="mt-1 text-[10px] text-kib-muted">
            Describe what you want. Grok builds it and ranks the top 25 — no preset or minimum
            pickers.
          </p>
          <label className="mt-3 block text-[11px] text-kib-muted">
            Your idea
            <textarea
              value={grokPrompt}
              onChange={(ev) => setGrokPrompt(ev.target.value)}
              rows={3}
              maxLength={1200}
              placeholder="e.g. Names tied to this X post about AI optics supply chain…"
              className="mt-1 min-h-[88px] w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 text-base text-kib-fg sm:text-sm"
            />
          </label>
          {editingId != null ? (
            <label className="mt-3 block text-[11px] text-kib-muted">
              Name
              <input
                type="text"
                value={formName}
                onChange={(ev) => setFormName(ev.target.value)}
                maxLength={80}
                className="mt-1 min-h-[44px] w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 text-base text-kib-fg sm:min-h-0 sm:py-2 sm:text-sm"
              />
            </label>
          ) : null}
          {grokRationale ? (
            <p className="mt-2 text-[11px] leading-snug text-kib-fg/85">{grokRationale}</p>
          ) : null}
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              disabled={grokBusy || formBusy}
              onClick={() => void onBuildStrategy()}
              className="btn-primary min-h-[44px] w-full text-sm disabled:opacity-50 sm:w-auto"
            >
              {grokBusy || formBusy
                ? 'Building…'
                : editingId != null
                  ? 'Rebuild with Grok'
                  : 'Build strategy'}
            </button>
            {editingId != null ? (
              <button
                type="button"
                disabled={formBusy || grokBusy}
                onClick={() => void onSaveStrategy()}
                className="btn-secondary min-h-[44px] w-full text-sm disabled:opacity-50 sm:w-auto"
              >
                Save name
              </button>
            ) : null}
            <button
              type="button"
              disabled={formBusy || grokBusy}
              onClick={() => setEditorOpen(false)}
              className="btn-secondary min-h-[44px] w-full text-sm sm:w-auto"
            >
              Cancel
            </button>
            {editingId != null && selectedCustom && selectedCustom.id === editingId ? (
              <button
                type="button"
                disabled={formBusy || grokBusy}
                onClick={() => void onDeleteStrategy(selectedCustom)}
                className="min-h-[44px] w-full px-3 text-sm text-rose-300 hover:underline disabled:opacity-50 sm:w-auto"
              >
                Delete
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="mb-3 space-y-2 rounded-lg border border-white/[0.08] bg-kib-surface px-3 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold text-kib-fg">Backtest panel — top 5 vs SPY</p>
          <button
            type="button"
            onClick={() => void loadBacktest()}
            disabled={backtestLoading}
            className="text-[11px] text-kib-cyber hover:underline disabled:opacity-50"
          >
            {backtestLoading ? 'Computing…' : 'Refresh backtest'}
          </button>
        </div>
        {backtest?.top_symbols_today?.length ? (
          <p className="text-[10px] text-kib-muted">
            Today&apos;s top 5:{' '}
            {backtest.top_symbols_today.map((sym, i) => (
              <React.Fragment key={sym}>
                {i > 0 ? ', ' : null}
                <Link
                  to={`/charts?symbol=${encodeURIComponent(sym)}`}
                  className="font-mono text-kib-fg/90 underline-offset-2 hover:text-kib-cyber hover:underline"
                  title={`Open Stock Charts for ${sym}`}
                >
                  {sym}
                </Link>
              </React.Fragment>
            ))}
          </p>
        ) : null}
        {backtestError ? <p className="text-[11px] text-amber-200">{backtestError}</p> : null}
        {backtestLoading && !backtest ? (
          <p className="text-[11px] text-kib-muted">Loading holdout backtest (cached ~6h)…</p>
        ) : (
          <div className="grid gap-2 lg:grid-cols-2">
            <BacktestHorizonTable
              title="Trailing — today's top 5"
              horizons={backtest?.trailing}
              note="Equal-weight return of current top picks over the lookback window."
            />
            <BacktestHorizonTable
              title="Holdout — re-ranked at window start"
              horizons={backtest?.holdout}
              note={backtest?.holdout?.disclaimer || 'Tape-only historical re-rank; educational research.'}
            />
          </div>
        )}
      </div>

      {outcomes && outcomes.totalLogged > 0 ? (
        <div className="mb-3 rounded-lg border border-white/[0.06] bg-kib-surface px-3 py-2 text-[11px] text-kib-muted">
          <p className="font-semibold text-kib-fg">Your suggestion adds ({outcomes.totalLogged})</p>
          <p className="mt-1">
            Avg return since add:{' '}
            <span className={pctClass(outcomes.avgReturnPct)}>{fmtPct(outcomes.avgReturnPct)}</span>
            {' · '}
            SPY over same periods:{' '}
            <span className={pctClass(outcomes.avgSpyReturnPct)}>{fmtPct(outcomes.avgSpyReturnPct)}</span>
            {' · '}
            Excess:{' '}
            <span className={pctClass(outcomes.avgExcessReturnPct)}>{fmtPct(outcomes.avgExcessReturnPct)}</span>
          </p>
          {outcomes.items?.length ? (
            <p className="mt-2 flex flex-wrap gap-x-2 gap-y-1">
              {outcomes.items.slice(0, 12).map((it) => (
                <Link
                  key={`${it.symbol}-${it.strategy}-${it.ageDays}`}
                  to={`/charts?symbol=${encodeURIComponent(it.symbol)}`}
                  className="font-mono text-kib-fg/90 underline-offset-2 hover:text-kib-cyber hover:underline"
                  title={`Open Stock Charts for ${it.symbol}`}
                >
                  {it.symbol}
                  <span className={`ml-1 tabular-nums ${pctClass(it.returnPct)}`}>
                    {fmtPct(it.returnPct)}
                  </span>
                </Link>
              ))}
            </p>
          ) : null}
        </div>
      ) : null}

      {disclaimer ? (
        <p className="mb-3 rounded-md border border-white/[0.08] bg-kib-surface px-3 py-2 text-[11px] leading-relaxed text-kib-muted whitespace-pre-wrap">
          {disclaimer.replace(/\*\*/g, '')}
        </p>
      ) : null}

      {meta ? (
        <div className="mb-3 grid gap-2 rounded-lg border border-white/[0.06] bg-kib-surface px-3 py-2 text-xs text-kib-muted sm:grid-cols-2 lg:grid-cols-4">
          <p>
            Accepted: <span className="font-medium text-emerald-400">{meta.accepted_count}</span>
          </p>
          <p>
            Excluded: <span className="font-medium text-amber-300">{meta.excluded_count}</span>
          </p>
          <p>
            Min price: <span className="font-medium text-kib-fg">${meta.min_price.toFixed(2)}</span>
          </p>
          <p>
            Min volume:{' '}
            <span className="font-medium text-kib-fg">
              ${Math.round(meta.min_avg_dollar_vol_20d).toLocaleString()}
            </span>
          </p>
        </div>
      ) : null}

      {error ? <p className="mb-3 text-sm text-amber-200">{error}</p> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {showEmptyLoading ? (
          <p className="text-sm text-kib-muted">Scanning broad stock universe and ranking candidates…</p>
        ) : positions.length === 0 ? (
          <p className="text-sm text-kib-muted">No ranked candidates for this strategy right now.</p>
        ) : (
          positions.slice(0, 9).map((row, idx) => {
            const rbLegs = ruleBreakerBreakdown(row.strategy_factors);
            const antiChase = antiChaseFromFactors(row.strategy_factors);
            return (
              <article
                key={row.symbol}
                className="rounded-lg border border-white/[0.08] bg-kib-surface/80 px-3 py-3"
              >
                <div className="mb-1 flex items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Link
                      to={`/charts?symbol=${encodeURIComponent(row.symbol)}`}
                      className="text-sm font-semibold font-mono text-kib-fg hover:text-kib-cyber"
                      title={`Open Stock Charts for ${row.symbol}`}
                    >
                      {row.symbol}
                    </Link>
                    {antiChase?.chase_risk ? (
                      <span
                        className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-amber-200"
                        title={(antiChase.reasons || []).join('; ')}
                      >
                        Extended
                      </span>
                    ) : null}
                  </div>
                  <span className="text-[11px] text-kib-cyber">Score {row.score.toFixed(2)}</span>
                </div>
                <p className="text-sm tabular-nums text-kib-fg">
                  {row.last_close == null ? '—' : row.last_close.toFixed(2)}
                </p>
                <p className={`text-xs tabular-nums ${pctClass(row.day_change_pct)}`}>
                  {row.day_change_pct == null
                    ? '—'
                    : `${row.day_change_pct >= 0 ? '+' : ''}${row.day_change_pct.toFixed(2)}%`}
                </p>
                <ul className="mt-2 space-y-1">
                  {row.why.slice(0, 4).map((reason) => (
                    <li key={reason} className="text-[11px] leading-relaxed text-kib-muted">
                      {reason}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-kib-muted">
                  Vol ${Math.round(row.avg_dollar_vol_20d ?? 0).toLocaleString()}
                </p>
                {rbLegs.length > 0 ? (
                  <ul className="mt-2 space-y-1.5 border-t border-white/[0.06] pt-2">
                    <li className="text-[10px] font-semibold uppercase tracking-wide text-amber-200/90">
                      Gardner checklist — leg scores (0–100) × weight
                    </li>
                    {rbLegs.map((leg) => (
                      <li key={leg.element_key} className="text-[10px] leading-snug text-kib-muted">
                        <span className="font-medium text-kib-fg/90">
                          {leg.element_key.replace(/_/g, ' ')}
                        </span>{' '}
                        <span className="text-kib-cyber/90">{leg.score_0_100.toFixed(0)}</span>
                        <span className="text-kib-muted/70">
                          {' '}
                          ×{(leg.weight * 100).toFixed(0)}% →{' '}
                        </span>
                        <span className="text-emerald-400/90">{leg.weighted_contribution.toFixed(2)}</span>
                        {leg.book_criterion ? (
                          <span className="mt-0.5 block text-[9px] text-kib-muted/80">{leg.book_criterion}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[11px] text-kib-muted">{row.position_hint}</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/charts?symbol=${encodeURIComponent(row.symbol)}`}
                      className="rounded-md border border-white/[0.12] px-2.5 py-1 text-[11px] font-medium text-kib-fg/90 transition hover:border-kib-cyber/40 hover:text-kib-cyber"
                      title={`Open Stock Charts for ${row.symbol}`}
                    >
                      Stock details
                    </Link>
                    <button
                      type="button"
                      onClick={() =>
                        void onAdd(row.symbol, row.score, idx + 1, row.last_close)
                      }
                      disabled={Boolean(adding[row.symbol] || added[row.symbol])}
                      className="rounded-md border border-kib-cyber/40 bg-kib-cyber/10 px-2.5 py-1 text-[11px] font-medium text-kib-cyber transition hover:bg-kib-cyber/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {added[row.symbol] ? 'Added' : adding[row.symbol] ? 'Adding…' : 'Add to watchlist'}
                    </button>
                  </div>
                </div>
                <div className="mt-2 border-t border-white/[0.06] pt-2 text-[11px] leading-relaxed text-kib-muted">
                  Why Quant suggests this:{' '}
                  {row.why[0] || 'Composite rank from momentum, volatility, and drawdown.'}
                </div>
              </article>
            );
          })
        )}
      </div>

      <p className="mt-3 border-t border-white/[0.06] pt-3 text-[11px] leading-relaxed text-kib-muted">
        Educational only — not investment advice. Ranks come from Quant AGI sidecar rules, not Grok recall.
        {loading && positions.length > 0 ? ' Updating ranks…' : null}
      </p>
    </section>
  );
};
